
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Check, X } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

interface Tag {
  id: string;
  name: string;
  category: string | null;
  featured?: boolean | null;
}

interface ItineraryTagSelectorProps {
  selectedTagIds: string[];
  onChange: (ids: string[]) => void;
}

export function ItineraryTagSelector({ selectedTagIds = [], onChange }: ItineraryTagSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  
  // Fetch all available tags
  const { data: tags, isLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .order('name');
        
      if (error) throw error;
      return data as Tag[];
    },
  });
  
  // Filter tags based on search query
  const filteredTags = tags?.filter(tag => 
    tag.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (tag.category && tag.category.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Toggle tag selection
  const toggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter(id => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  };
  
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="tag-search">Search Tags</Label>
        <Input
          id="tag-search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by tag name or category..."
        />
      </div>
      
      {selectedTagIds.length > 0 && (
        <div className="space-y-2">
          <Label>Selected Tags</Label>
          <div className="flex flex-wrap gap-2">
            {tags?.filter(tag => selectedTagIds.includes(tag.id)).map(tag => (
              <Badge 
                key={tag.id} 
                variant="secondary"
                className="flex items-center gap-1 py-1.5"
              >
                {tag.name}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 text-muted-foreground"
                  onClick={() => toggleTag(tag.id)}
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">Remove</span>
                </Button>
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      <Card>
        <CardContent className="p-4">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : filteredTags?.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No tags found</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {filteredTags?.map(tag => {
                const isSelected = selectedTagIds.includes(tag.id);
                return (
                  <Button
                    key={tag.id}
                    type="button"
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    className={`justify-start gap-2 ${tag.featured ? 'border-primary-500' : ''}`}
                    onClick={() => toggleTag(tag.id)}
                  >
                    {isSelected && <Check className="h-3.5 w-3.5" />}
                    <span className="truncate">{tag.name}</span>
                  </Button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
